/**
 * event-view.js
 */

import React from 'react';
import axios from 'axios';
import { Grid, Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { makeLocation } from 'utils/routing';
import { formatWeatherSummary } from 'utils/weather';
import useMounted from 'utils/use-mounted';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerContent from 'components/menu-drawer/drawer-content';
import EditButton from 'components/edit-button';
import FeatureBox from 'components/feature-box';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import IconSection from 'components/icon-section';
import Icon from 'components/icon';
import StaticMap from 'components/static-map';
import TrainingWizardComplete from 'components/training/training-drawer/training-wizard-complete';
import { AttendanceIcon } from 'components/event-attendance/event-attendance';
import RecordFooter from 'components/record-footer';


const useStyles = makeStyles(theme => ({
  locationContainer: {
    maxWidth: 320,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: 'none'
    }
  },
  mapContainer: {
    display: 'flex', 
    marginBottom: theme.spacing(3),
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-4),
      marginLeft: theme.spacing(5),
    }
  },
  memberList: {
    '& .row': {
      display: 'flex',
      margin: `3px ${theme.spacing(1)}`,
      marginLeft: 0,
    },
    '& .header': {
      color: theme.palette.grey.colors[6],
      marginBottom: 4,
    },
    '& .attendance': {
      display: 'flex',
      marginRight: theme.spacing(1)
    },
  },
  weather: {
    marginTop: theme.spacing(2),
  },
}));


// use wrapper to remount component when event ID changes
export default function EventView(props) {
  const { eventId } = useParams();

  return <EventViewImplementation
    eventId={eventId}
    key={eventId}
    {...props}
  />;
}


function EventViewImplementation(props) {
  const {
    eventId,
    drawerState,
    onClose,
    basePath
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  let history = useHistory();
  const isMounted = useMounted();
  const { t } = useTranslation('general');
  const recordStore = useRecordStore();
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId).ornull;
  const hasRecord = Boolean(eventRecord);
  const venueContact = eventRecord && eventRecord.get().venueContact
    ? `Contact: ${eventRecord.get().venueContact}` : '';
  const eventLocation = eventRecord?.get()?.location;
  const { address1, city, region, country, postCode } = eventLocation || {};
  const eventDate = eventRecord?.get()?.date;
  const [weatherSummary, setWeatherSummary] = React.useState(null);
  const [weatherLoading, setWeatherLoading] = React.useState(false);


  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(!hasRecord));

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord]);


  // fetch weather info
  // possible weatherSummary values:
  // 'null'             : not applicable, i.e. event is past, or before initializing
  // 'NOT AVAILABLE'    : error in last API call
  // formatted weather  : last API call successful
  React.useEffect(() => {
    if (address1 && city && region && country && eventDate && (new Date() < new Date(eventDate))) {
      (async () => {
        setWeatherLoading(true)
        try {
          const response = await axios.get('/weather/address', {
            params: { address1, city, region, country, postCode, datetime: eventDate },
            bypassErrorHandling: true
          });
          if (!isMounted.current) return;
          if (response.status !== 200) {
            setWeatherSummary('NOT AVAILABLE')
          };
          const weather = response.data;
          setWeatherSummary(formatWeatherSummary(weather))
        } catch (e) {
          if (!isMounted.current) return;
          setWeatherSummary('NOT AVAILABLE')
        } finally {
          setWeatherLoading(false);
        }
      })();
    } else {
      setWeatherSummary(null);
    }
  }, [address1, city, region, country, postCode, eventDate, isMounted]);


  const onBack = () => {
    history.push(makeLocation(
      basePath,
      'training',
      'menu',
      {type: 'event', id: eventId},
      history.location
    ));
  }


  const backButton = (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onBack}
      children={<Icon type='nav-back' />}
    />
  );

  
  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
      children={<Icon type='mobile-close-drawer' />}
    />
  );


  const getEditButton = (isMobile) => !user.role.isSupervisor ? (
    <EditButton
      tooltip={eventRecord?.get()?.canEdit ? '' : t('NoEditPermission')}
      disabled={!user.role.status.isActive || !eventRecord?.get()?.canEdit}
      iconOnly={isMobile}
      data-testid='edit-event-button'      
      to={makeLocation(
        basePath,
        'training',
        'editEvent',
        {eventId},
        history.location
      )}
    />
  ) : null;


  const generateAddress = (location) => {
    return (<>
      {location.address1} <br/>
      {`${location.city}, ${location.region}`} <br/>
      {location.postCode}
    </>);
  }


  return (
    <>
      {eventRecord &&
      <DrawerContentBuilder
        mobileAppBar={{
          title: 'Event',
          leftActions: backButton,
          rightActions: <>{closeButton}{getEditButton(true)}</>,
          recordGroupType: Types.RecordGroup.TRAINING,
        }}
        desktopHeader={{
          title: eventRecord.get().name,
          actions: getEditButton(false)
        }}
        children={(triggerElement, desktopHeader) => (<>
          {/* wizard completion card */}
          {!shouldRenderMobileLayout() &&
          <TrainingWizardComplete basePath={basePath} />}
          
          {desktopHeader}
          <MobilePageTitle title={eventRecord.get().name} />
          <DrawerContent>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} className={classes.locationContainer}>

                {/* time */}
                <IconSection iconName='event'>
                  <Typography variant="body1">
                    {format(parseISO(eventRecord.get().date), 'MMM dd, yyyy h:mm aaa')}
                  </Typography>
                  <Typography>
                    {eventRecord.get().duration} Hour{`${eventRecord.get().duration === 1 ? '' : 's'}`}
                  </Typography>
                </IconSection>

                {/* location */}
                <IconSection iconName='location'>
                  <Typography variant="body1" style={{fontWeight: 500}}>
                    {eventRecord.get().location.locationName || 'no address'}
                  </Typography>
                  <Typography>
                    {generateAddress(eventRecord.get().location)}
                  </Typography>
                  <Typography>
                    {venueContact}
                  </Typography>

                  {/* weather for location */}
                  {(weatherSummary !== null || weatherLoading) &&
                    <div className={classes.weather}>
                      <Typography style={{fontWeight: 500}}>
                        Forecast
                      </Typography>
                        <FeatureBox loading={weatherLoading}>
                          <Typography>
                            {weatherSummary}
                          </Typography>
                        </FeatureBox>
                    </div>
                  }
                </IconSection>
              
              </Grid>

              {/* map */}
              {eventRecord.get().location.locationName &&
              <Grid 
                item 
                xs={12} 
                sm={6} 
                className={classes.mapContainer}
              >
                <StaticMap location={eventRecord.get().location} />
              </Grid>}

            </Grid>


            {/* members */}
            <IconSection iconName='group'>
              <div className={classes.memberList}>
                {eventRecord.get().members.map(member =>
                  <div 
                    key={member.id}
                    className='row member'
                  >
                    <AttendanceIcon 
                      isOptional={member.isOptional} 
                      isAttending={member.isAttending}
                      label={`${member.name}${member.activeDogCount === 0 ? ' (NO ACTIVE DOGS)' : ''}`}
                      showTooltip
                    />
                  </div>)}
              </div>
            </IconSection>


            {/* tags */}
            {eventRecord.get().tags.length > 0 &&
            <IconSection iconName='tag'>
              {eventRecord.get().tags.map(tag =>
                <Typography
                  key={tag.id}
                  variant="body2"
                >
                  {tag.name}
                </Typography>)}
            </IconSection>}


            {/* description */}
            {eventRecord.get().description &&
            <IconSection iconName='description'>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {eventRecord.get().description}
              </Typography>
            </IconSection>}

            <RecordFooter
              createdBy={eventRecord.get().creator.name}
              dateCreated={eventRecord.get().dateCreated}
            />
          </DrawerContent>
        </>)}
      />}
    </>
  );
}